/**
 * LoggingTour-specific types and step definitions.
 *
 * The LoggingTour walks users through Voley's smart-logging system —
 * manual logging to their AMS, text vs link formats, task creation,
 * MMS attachment logging, and automated end-of-day logging settings.
 */

import type { TourStep } from '../types';

/** Unique identifiers for each LoggingTour step */
export type LoggingTourStepId =
  | 'logging_welcome'
  | 'highlight_log_btn'
  | 'highlight_logging_panel'
  | 'highlight_log_selected'
  | 'highlight_text_posting'
  | 'highlight_link_posting'
  | 'highlight_tasks_mms'
  | 'highlight_log_on_close'
  | 'highlight_end_of_day'
  | 'logging_complete';

/** Ordered list of all LoggingTour steps */
export const LOGGING_TOUR_STEPS: TourStep[] = [
  {
    id: 'logging_welcome',
    type: 'modal',
    title: '',
    content: '',
  },
  {
    id: 'highlight_log_btn',
    type: 'highlight',
    targetSelector: '[data-tour-id="log-btn"]',
    title: 'Start Logging',
    content:
      'Click the Log button to enter logging mode. This opens the selection panel where you can choose exactly which messages to log to your AMS.',
    placement: 'bottom',
    advanceOnElement: '[data-tour-id="logging-panel"]',
  },
  {
    id: 'highlight_logging_panel',
    type: 'highlight',
    targetSelector: '[data-tour-id="logging-panel"]',
    title: 'Select Messages',
    content:
      'Use "Select All New" to grab every unlogged message, or click individual message checkboxes for specific ones. Already-logged messages are greyed out so you never double-log. Try selecting some messages now.',
    placement: 'bottom',
    allowInteraction: true,
  },
  {
    id: 'highlight_log_selected',
    type: 'highlight',
    targetSelector: '[data-tour-id="logging-panel"]',
    title: 'Log to Your AMS',
    content:
      'Once you\'ve selected messages, "Log Selected" opens a confirmation where you choose your log format, add an optional title, attach a follow-up task, or include files. The log is posted directly to the client\'s profile in your AMS.',
    placement: 'bottom',
  },
  {
    id: 'highlight_text_posting',
    type: 'highlight',
    targetSelector: '[data-tour-id="logging-panel"]',
    title: 'Text Posting Format',
    content:
      'Posting as "Text Content" logs the full conversation directly into your AMS client profile. Every message includes a timestamp and sender name — for example: "[1/15/2026 2:30 PM] John Smith: Can you send me a quote?" This makes conversations fully searchable inside your AMS.',
    placement: 'bottom',
  },
  {
    id: 'highlight_link_posting',
    type: 'highlight',
    targetSelector: '[data-tour-id="logging-panel"]',
    title: 'Link Posting Format',
    content:
      'Posting as "Link" saves a clean reference in your AMS that opens a formatted view of the logged conversation. This is ideal for longer exchanges — it keeps your AMS tidy while giving your team one-click access to the full conversation with timestamps and context.',
    placement: 'bottom',
  },
  {
    id: 'highlight_tasks_mms',
    type: 'highlight',
    targetSelector: '[data-tour-id="logging-panel"]',
    title: 'Tasks & Attachments',
    content:
      'When logging, you can create a follow-up task directly in your AMS — assign it to a CSR, producer, or specific user with a due date and description. Any MMS images or file attachments in the conversation are automatically uploaded alongside the log. No separate steps needed.',
    placement: 'bottom',
  },
  {
    id: 'highlight_log_on_close',
    type: 'highlight',
    targetSelector: '[data-tour-id="log-on-close-setting"]',
    title: 'Log on Close',
    content:
      'When enabled, closing a conversation automatically prompts your team to log any unlogged messages first. This ensures nothing slips through the cracks — every resolved conversation gets documented before it leaves your inbox.',
    placement: 'bottom',
  },
  {
    id: 'highlight_end_of_day',
    type: 'highlight',
    targetSelector: '[data-tour-id="end-of-day-setting"]',
    title: 'End-of-Day Auto-Logging',
    content:
      'This is your safety net. At 11:59 PM daily, Voley automatically logs every unlogged message across all conversations to your AMS. Choose "Smart" format to auto-select text for short conversations and links for longer ones, or pick a fixed format. This catches anything your team forgot to log manually.',
    placement: 'bottom',
  },
  {
    id: 'logging_complete',
    type: 'modal',
    title: '',
    content: '',
  },
];
